import { ReactElement } from 'react';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import RefundBadge from '~/public/refund-badge.svg';
import { Heading, Link } from '@appsumo/dorado-react';

interface InfoSnippetURLProps {
  text: string;
  url: string;
}

interface InfoSnippetProps {
  title: string;
  text: string;
  urls?: Array<InfoSnippetURLProps>;
}

const INFO_SNIPPET_TEXT = [
  {
    title: 'Try thousands of products risk-free',
    text: ' Sumo-lings are protected by {{URL}}. We offer a 60-day money-back guarantee for qualifying purchases -- no matter the reason.',
    urls: [
      {
        text: 'our industry-best refund policy',
        url: 'https://help.appsumo.com/article/31-refund-policy',
      },
    ],
  },
  {
    title: 'World-class customer support ',
    text: "There's customer support, and then there's AppSumo customer support. We take pride in going above and beyond to keep our community happy.",
  },
  {
    title: 'Talk to founders and CEOs',
    text: ' Ask your burning questions on any active deal and get answers from the founders and C-level execs themselves.',
  },
] as Array<InfoSnippetProps>;

function InfoSnippet(): ReactElement {
  const snippets = INFO_SNIPPET_TEXT.map((snippet: InfoSnippetProps) => {
    let textList = [<span key={snippet.text}>{snippet.text}</span>];

    if (snippet.urls) {
      const splitText = snippet.text.split('{{URL}}');

      if (splitText.length > 1) {
        textList = [];

        for (let i = 0; i < splitText.length; i++) {
          const textPartial = splitText[i];
          textList.push(<span key={textPartial}>{textPartial}</span>);

          if (!(i % 2)) {
            const { url, text } = snippet.urls?.[i] || {};

            textList.push(
              <Link
                href={url}
                key={url}
                target="_blank"
                rel="noreferrer"
                styled
              >
                {text}
              </Link>,
            );
          }
        }
      }
    }

    return (
      <div key={snippet.title} className="flex">
        <FontAwesomeIcon
          icon={faCheckCircle}
          className="mt-[2px] text-dollar"
        />
        <div className="ml-2 grow text-xs">
          <span className="font-bold">{snippet.title}</span>
          <span className="font-light">{textList}</span>
        </div>
      </div>
    );
  }) as Array<ReactElement>;

  return (
    <div className="flex flex-col gap-2 text-xs" data-testid="summary-snippets">
      {snippets}
    </div>
  );
}

export default function Guarantee(): ReactElement {
  return (
    <div className="relative">
      <div className="absolute inset-0 z-0 rounded bg-dollar opacity-10" />
      <div className="relative z-0 px-4 pb-6 pt-3">
        <div className="flex w-full flex-col items-center">
          <Image
            alt="refund-badge"
            src={RefundBadge}
            width="48"
            height="48"
            className="h-12 w-12"
          />
          <Heading.H6 className="my-4">
            1.5+ million entrepreneurs helped
          </Heading.H6>
        </div>
        <InfoSnippet />
      </div>
    </div>
  );
}
